import React, { useState } from 'react';
import { PaymentPlan } from 'api/types/owner';
import { Button, Card, CardContent, Link, Typography } from 'ui-kit';
import PlanFeatures from './PlanFeatures';
import { createPaymentPlanCheckout } from 'api/stripe';
import { message } from 'antd';
import clsx from 'clsx';
import { useAuth } from 'contexts/auth/auth';
import { Role } from 'api/types/auth';
import ConfirmModal from 'components/ConfirmModal';
import _ from 'lodash';

const getMonthlyPrice = (price: number, interval: 'month' | 'year') => {
  if (interval === `year`) {
    const monthlyPrice = price / 12;
    return parseFloat(monthlyPrice.toFixed(2));
  }
  return parseFloat(price.toFixed(2));
};

type Props = {
  image: string;
  planName: string;
  planType: PaymentPlan;
  price: number;
  interval: 'month' | 'year';
  priceId?: string;
  selected: boolean;
  onManage: () => void;
  buttonText: string;
  ownerPlan?: PaymentPlan;
  ownerPlanOverride?: PaymentPlan;
};

export default function PlanCard({
  image,
  planName,
  planType,
  price,
  interval,
  priceId,
  selected,
  onManage,
  buttonText,
  ownerPlan,
  ownerPlanOverride,
}: Props) {
  const { role } = useAuth();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const handleClick = () => {
    if (planType === PaymentPlan.Basic) {
      window.open('https://www.thehost.co/get-started', '_blank');
      return;
    }
    if (role !== Role.HOST) {
      setIsModalVisible(true);
      return;
    }
    if (selected) {
      onManage();
      return;
    }
    if (ownerPlanOverride) {
      message.warning(
        `Your plan is permanently set to ${ownerPlanOverride.replace(
          /_/g,
          ' ',
        )}. Contact support@thehost.co, if you want to change it!`,
      );
      return;
    }

    if (ownerPlan) {
      onConfirmPlanChange();
    }
  };

  const onConfirmPlanChange = () => {
    const toltCustomerId = _.get(window, 'tolt_data.customer_id', null);

    createPaymentPlanCheckout(priceId, toltCustomerId).catch((err) => {
      message.error(err.message);
    });
  };
  return (
    <Card className={clsx('PlanCard', { selected })}>
      <div className="PlanCardHeader">
        <img className="PlanCardLogo" src={image} />
        <Typography className="PlanCardName" variant="h4">
          {planName}
        </Typography>
      </div>
      <CardContent>
        <div className="PlanCardPriceCtn">
          <div className="PlanCardPrice">
            <Typography className="PlanCardPriceValue" variant="h5">
              ${getMonthlyPrice(price, interval)}
            </Typography>
            &nbsp;
            <Typography className="PlanCardPriceInterval" variant="subtitle2">
              /month
            </Typography>
          </div>
          <Typography className="PlanCardPriceCtnInterval" variant="caption">
            {planType !== PaymentPlan.Basic
              ? interval === `year`
                ? 'billed once yearly'
                : `\u00A0`
              : '\u00A0'}
          </Typography>
        </div>

        <Button
          variant="contained"
          color="primary"
          size="medium"
          className="PlanCardSubscribeButton"
          onClick={handleClick}
        >
          {buttonText}
        </Button>
        <PlanFeatures plan={planType} />
      </CardContent>
      <ConfirmModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
        onConfirm={() => {
          window.location.href = '/login';
        }}
        confirmText="Sign In"
        cancelText="Close"
      >
        <Typography variant="h6">
          To subscribe, please
          <Link to="/login">sign in to your account.</Link>
        </Typography>
      </ConfirmModal>
    </Card>
  );
}
